/**
 * CreatePRModal - Modal for creating a pull request.
 */

import { useState, useEffect } from "react";
import { useGitStore } from "@/stores/gitStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, GitPullRequest, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Branch {
  name: string;
  sha: string;
  protected: boolean;
}

interface CreatePRModalProps {
  owner: string;
  repo: string;
  branches: Branch[];
  currentBranch: string | null;
}

export function CreatePRModal({
  owner,
  repo,
  branches,
  currentBranch,
}: CreatePRModalProps) {
  const { createPullRequest, isSaving } = useGitStore();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [head, setHead] = useState(currentBranch || "");
  const [base, setBase] = useState("");
  const [draft, setDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPR, setCreatedPR] = useState<{ number: number; url: string } | null>(null);

  // Set default base to main/master
  useEffect(() => {
    if (open && !base) {
      const defaultBase = branches.find(
        (b) => b.name === "main" || b.name === "master"
      );
      if (defaultBase) {
        setBase(defaultBase.name);
      } else if (branches.length > 0) {
        setBase(branches[0].name);
      }
    }
  }, [open, branches, base]);

  // Update head when currentBranch changes
  useEffect(() => {
    if (currentBranch && currentBranch !== base) {
      setHead(currentBranch);
    }
  }, [currentBranch, base]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (!head || !base) {
      setError("Please select both source and target branches");
      return;
    }

    if (head === base) {
      setError("Source and target branches must be different");
      return;
    }

    const result = await createPullRequest(
      owner,
      repo,
      title.trim(),
      body.trim(),
      head,
      base,
      draft
    );

    if (result) {
      setCreatedPR({ number: result.number, url: result.url });
      toast({
        title: "Pull request created",
        description: `PR #${result.number} has been created successfully.`,
      });
    } else {
      const storeError = useGitStore.getState().error;
      setError(storeError?.message || "Failed to create pull request");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTitle("");
    setBody("");
    setDraft(false);
    setError(null);
    setCreatedPR(null);
  };

  // Filter out the base branch from head options
  const headBranches = branches.filter((b) => b.name !== base);
  const baseBranches = branches.filter((b) => b.name !== head);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => (isOpen ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <GitPullRequest className="w-4 h-4 mr-2" />
          Create PR
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        {createdPR ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <GitPullRequest className="w-5 h-5" />
                Pull Request Created!
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="mb-4">
                Pull request #{createdPR.number} has been created successfully.
              </p>
              <Button asChild className="w-full">
                <a href={createdPR.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View on GitHub
                </a>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GitPullRequest className="w-5 h-5" />
                Create Pull Request
              </DialogTitle>
              <DialogDescription>
                Open a pull request to merge changes into another branch
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From (head)</Label>
                  <Select value={head} onValueChange={setHead} disabled={isSaving}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {headBranches.map((branch) => (
                        <SelectItem key={branch.name} value={branch.name}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>To (base)</Label>
                  <Select value={base} onValueChange={setBase} disabled={isSaving}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {baseBranches.map((branch) => (
                        <SelectItem key={branch.name} value={branch.name}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pr-title">Title</Label>
                <Input
                  id="pr-title"
                  placeholder="Add new feature"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pr-body">Description (optional)</Label>
                <Textarea
                  id="pr-body"
                  placeholder="Describe your changes..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  disabled={isSaving}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="draft"
                  checked={draft}
                  onCheckedChange={(checked) => setDraft(checked === true)}
                  disabled={isSaving}
                />
                <Label htmlFor="draft" className="text-sm font-normal">
                  Create as draft pull request
                </Label>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !title.trim() || !head || !base}>
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Pull Request
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
